import {createSelector} from 'reselect';

// Import from the context module directly (not the SingleRunMode barrel) to
// avoid a module-initialization cycle back into the settings hooks.
import {useSingleRunModeContext} from '../../../../MultiRunWorkspace/SingleRunMode/SingleRunModeContext';
import {type MediaBrowserPanelConfig} from '../../../../PanelMediaBrowser/common';
import {
  DEFAULT_MAX_Y_AXIS_COUNT,
  defaultColumnCount,
} from '../../../../PanelMediaBrowser/config';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from '../../runLinePlots/maxRunDefaults';
import {SettingsLevel} from '../types';
import {
  makeMediaSettingHooks,
  type MediaSettingHooks,
} from './makeMediaSettingHooks';
import {
  ALL_SETTINGS_LEVELS,
  type FieldValue,
  makeMediaSettingSelectorFamily,
  type MediaSettingSelectorFamily,
  type MediaSettingSpec,
  type ResolvedValue,
} from './makeMediaSettingSelectorFamily';

type Object3DBackgroundColor = NonNullable<
  MediaBrowserPanelConfig['object3DBackgroundColor']
>;

// Default background color for Babylon Scene is 0.2, 0.2, 0.3, 1 on a scale of 0-1. This converts it to 0-255.
export const OBJECT3D_BACKGROUND_COLOR_DEFAULT: Object3DBackgroundColor = {
  r: Math.floor(0.2 * 255),
  g: Math.floor(0.2 * 255),
  b: Math.floor(0.3 * 255),
  a: 1,
};

type MediaSetting<
  K extends keyof MediaBrowserPanelConfig,
  R,
  D = R,
  P = void,
> = {
  selectors: MediaSettingSelectorFamily<K, R, D, P>;
  hooks: MediaSettingHooks<K, R, D>;
  /** The levels this setting can be set at (source of truth for the drawer UI). */
  levels: SettingsLevel[];
};

function makeMediaSetting<K extends keyof MediaBrowserPanelConfig>(
  spec: MediaSettingSpec<K> & {
    defaultValue: NonNullable<MediaBrowserPanelConfig[K]>;
  }
): MediaSetting<K, ResolvedValue<K>>;
function makeMediaSetting<K extends keyof MediaBrowserPanelConfig, P>(
  spec: MediaSettingSpec<K, P> & {
    getDefault: (params: P) => ResolvedValue<K>;
    /** Hook supplying the params (e.g. isSingleRun) from context. */
    useParams: () => P;
  }
): MediaSetting<K, ResolvedValue<K>, ResolvedValue<K>, P>;
function makeMediaSetting<K extends keyof MediaBrowserPanelConfig>(
  spec: MediaSettingSpec<K> & {
    makeDisplayDefaultSelector: NonNullable<
      MediaSettingSpec<K>['makeDisplayDefaultSelector']
    >;
  }
): MediaSetting<K, FieldValue<K>, ResolvedValue<K>>;
function makeMediaSetting<K extends keyof MediaBrowserPanelConfig>(
  spec: MediaSettingSpec<K>
): MediaSetting<K, MediaBrowserPanelConfig[K]>;
function makeMediaSetting<K extends keyof MediaBrowserPanelConfig, P>(
  spec: MediaSettingSpec<K, P> & {useParams?: () => P}
): MediaSetting<K, FieldValue<K>, FieldValue<K>, P> {
  const {useParams, ...selectorSpec} = spec;
  const selectors = makeMediaSettingSelectorFamily(
    selectorSpec
  ) as MediaSettingSelectorFamily<K, FieldValue<K>, FieldValue<K>, P>;
  return {
    selectors,
    hooks: makeMediaSettingHooks(selectors, useParams),
    levels: spec.levels ?? ALL_SETTINGS_LEVELS,
  };
}

type GallerySettings = NonNullable<MediaBrowserPanelConfig['gallerySettings']>;
type GridSettings = NonNullable<MediaBrowserPanelConfig['gridSettings']>;

const GALLERY_SETTINGS_DEFAULT_SINGLE_RUN: GallerySettings = {axis: 'index'};
const GALLERY_SETTINGS_DEFAULT_MULTI_RUN: GallerySettings = {axis: 'run'};

const GRID_SETTINGS_DEFAULT_SINGLE_RUN: GridSettings = {
  xAxis: 'step',
  yAxis: 'index',
};
const GRID_SETTINGS_DEFAULT_MULTI_RUN: GridSettings = {
  xAxis: 'step',
  yAxis: 'run',
};

const useIsSingleRun = (): boolean => useSingleRunModeContext().isSingleRun;

// Extracted so columnCount's display default can read the effective mode.
const mode = makeMediaSetting({
  field: 'mode',
  defaultValue: 'gallery',
});

/**
 * Table of media settings generated by the settings factory. One row per
 * field; the named exports in `mediaSettingsSelectors.ts` /
 * `hooks/mediaSettingHooks.ts` stay as thin wrappers over
 * `mediaSettings.<field>` so call sites don't churn.
 *
 * Not in the registry (still hand-written): per-field extras
 * (showOverrides / componentKey / isInherited selectors), which compose the
 * generated selectors.
 */
export const mediaSettings = {
  actualSize: makeMediaSetting({
    field: 'actualSize',
    defaultValue: false,
  }),
  alwaysOriginal: makeMediaSetting({
    field: 'alwaysOriginal',
    defaultValue: false,
  }),
  boundingBoxConfig: makeMediaSetting({
    field: 'boundingBoxConfig',
  }),
  chartTitle: makeMediaSetting({
    field: 'chartTitle',
    // Panel-only: it never cascades from workspace or section.
    levels: [SettingsLevel.PANEL],
  }),
  columnCount: makeMediaSetting({
    field: 'columnCount',
    // Default depends on the effective media layout mode at the same level.
    makeDisplayDefaultSelector: (
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ) =>
      createSelector(
        mode.selectors.makeDisplaySelector(
          workspaceSettingsRef,
          sectionRef,
          settingsLevel,
          panelRef
        ),
        modeValue => defaultColumnCount(modeValue)
      ),
  }),
  compareSettings: makeMediaSetting({
    field: 'compareSettings',
  }),
  disableVideoStream: makeMediaSetting({
    field: 'disableVideoStream',
    defaultValue: false,
  }),
  fitToDimension: makeMediaSetting({
    field: 'fitToDimension',
    defaultValue: false,
  }),
  gallerySettings: makeMediaSetting({
    field: 'gallerySettings',
    getDefault: (isSingleRun: boolean) =>
      isSingleRun
        ? GALLERY_SETTINGS_DEFAULT_SINGLE_RUN
        : GALLERY_SETTINGS_DEFAULT_MULTI_RUN,
    useParams: useIsSingleRun,
  }),
  gridSettings: makeMediaSetting({
    field: 'gridSettings',
    getDefault: (isSingleRun: boolean) =>
      isSingleRun
        ? GRID_SETTINGS_DEFAULT_SINGLE_RUN
        : GRID_SETTINGS_DEFAULT_MULTI_RUN,
    useParams: useIsSingleRun,
  }),
  maxGalleryItems: makeMediaSetting({
    field: 'maxGalleryItems',
    // "Show all" is stored as `null` so a child level can block an inherited
    // limit; plain child-wins would treat it as unset and keep inheriting.
    cascade: 'nullable-child-wins',
  }),
  maxRuns: makeMediaSetting({
    field: 'maxRuns',
    defaultValue: DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
  }),
  maxYAxisCount: makeMediaSetting({
    field: 'maxYAxisCount',
    defaultValue: DEFAULT_MAX_Y_AXIS_COUNT,
  }),
  mediaKeys: makeMediaSetting({
    field: 'mediaKeys',
    // Panel-only: every panel has media keys defined, so setting them at a
    // higher level would do nothing.
    levels: [SettingsLevel.PANEL],
  }),
  mediaSliderKey: makeMediaSetting({
    field: 'mediaSliderKey',
    defaultValue: '_step',
  }),
  mode,
  moleculeConfig: makeMediaSetting({
    field: 'moleculeConfig',
  }),
  object3DBackgroundColor: makeMediaSetting({
    field: 'object3DBackgroundColor',
    defaultValue: OBJECT3D_BACKGROUND_COLOR_DEFAULT,
  }),
  pixelated: makeMediaSetting({
    field: 'pixelated',
    defaultValue: true,
  }),
  pointColor: makeMediaSetting({
    field: 'pointColor',
  }),
  segmentationMaskConfig: makeMediaSetting({
    field: 'segmentationMaskConfig',
  }),
  sliderSync: makeMediaSetting({
    field: 'sliderSync',
    defaultValue: false,
    // No panel level: what would a single panel's slider sync with?
    levels: [SettingsLevel.WORKSPACE, SettingsLevel.SECTION],
  }),
  snapToExistingStep: makeMediaSetting({
    field: 'snapToExistingStep',
    defaultValue: true,
  }),
  stepStrideLength: makeMediaSetting({
    field: 'stepStrideLength',
  }),
  useRightHandedSystem: makeMediaSetting({
    field: 'useRightHandedSystem',
    defaultValue: false,
  }),
  videoActualSize: makeMediaSetting({
    field: 'videoActualSize',
    defaultValue: false,
  }),
  videoPlaybackSync: makeMediaSetting({
    field: 'videoPlaybackSync',
    defaultValue: false,
    // Enabling sync at a higher level locks the lower levels on.
    cascade: 'parent-wins',
  }),
  videoPlaybackSyncAutoplay: makeMediaSetting({
    field: 'videoPlaybackSyncAutoplay',
    defaultValue: false,
  }),
  videoPlaybackSyncLoop: makeMediaSetting({
    field: 'videoPlaybackSyncLoop',
    defaultValue: false,
  }),
  videoPlayerTimeStep: makeMediaSetting({
    field: 'videoPlayerTimeStep',
  }),
} as const;
